import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { trpcClient } from "@/lib/trpc";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ComboboxFK } from "@/components/shared/ComboboxFK";
import { useProcessLookup } from "@/hooks/useFKLookup";

const matrixSchema = z.object({
  processId: z.string().min(1, "Vui lòng chọn công đoạn"),
  minQuantity: z.coerce.number().min(0, "MOQ không hợp lệ"),
  wastePercentage: z.coerce
    .number()
    .min(0, "% Hao hụt phải >= 0")
    .max(100, "% Hao hụt tối đa 100%"),
  fixedWaste: z.coerce.number().min(0, "Hao hụt cố định phải >= 0"),
  isActive: z.boolean().default(true),
});

type FormValues = z.infer<typeof matrixSchema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: any | null;
}

export function WasteMatrixFormModal({ open, onOpenChange, item }: Props) {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { options: processOptions, isLoading: processesLoading } = useProcessLookup(open);
  const form = useForm<FormValues>({
    resolver: zodResolver(matrixSchema),
    defaultValues: {
      processId: "",
      minQuantity: 0,
      wastePercentage: 0,
      fixedWaste: 0,
      isActive: true,
    },
  });

  useEffect(() => {
    if (item) {
      form.reset({
        processId: item.processId || "",
        minQuantity: Number(item.moqFrom ?? item.minQuantity ?? 0),
        wastePercentage: Number(item.wastePercent ?? item.wastePercentage ?? 0),
        fixedWaste: Number(item.wasteFixed ?? item.fixedWaste ?? 0),
        isActive: item.isActive !== false,
      });
    } else {
      form.reset({
        processId: "",
        minQuantity: 0,
        wastePercentage: 0,
        fixedWaste: 0,
        isActive: true,
      });
    }
  }, [item, form, open]);

  const handleSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    try {
      if (item) {
        await trpcClient.wasteMatrix.update.mutate({
          id: item.id,
          data: {
            processId: values.processId,
            moqFrom: values.minQuantity,
            wastePercent: values.wastePercentage,
            wasteFixed: values.fixedWaste,
            isActive: values.isActive,
          },
        });
        toast.success("Cập nhật cấu hình hao hụt thành công");
      } else {
        await trpcClient.wasteMatrix.create.mutate({
          processId: values.processId,
          moqFrom: values.minQuantity,
          wastePercent: values.wastePercentage,
          wasteFixed: values.fixedWaste,
          isActive: values.isActive,
        });
        toast.success("Thêm mới cấu hình hao hụt thành công");
      }
      await queryClient.invalidateQueries({ queryKey: ["waste-matrix"] });
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err?.message || "Đã có lỗi xảy ra");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {item ? "Sửa Cấu Hình Hao Hụt" : "Thêm Cấu Hình Mới"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="processId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Công Đoạn</FormLabel>
                  <FormControl>
                    {/* processId là khoá ngoại tới danh mục công đoạn (bảng processes) — trước đây đọc danh mục cấu hình
                        "PROCESS" không tồn tại nên luôn trống (FB-YXGBRT) */}
                    <ComboboxFK
                      value={field.value}
                      onChange={(id) => field.onChange(id)}
                      options={processOptions}
                      loading={processesLoading}
                      placeholder="Chọn công đoạn"
                      canAdd={false}
                      hideLabel
                      dataField="processId"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="minQuantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>MOQ</FormLabel>
                    <FormControl>
                      <Input type="number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="wastePercentage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>% Hao Hụt</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.1" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="fixedWaste"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Hao Hụt Cố Định</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                  <div className="space-y-0.5">
                    <FormLabel>Trạng Thái Hoạt Động</FormLabel>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Hủy
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {item ? "Cập Nhật" : "Thêm Mới"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
